import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { EmptyState } from '@/components/common/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { classSubjectsQueryOptions } from '../api/question.api';
import type { AssignedSubjectPayload } from '../types/question.types';

interface SubjectTargetPickerProps {
  classId: number | undefined;
  deadlineHint: string | null;
  value: AssignedSubjectPayload[];
  onChange: (next: AssignedSubjectPayload[]) => void;
}

export function SubjectTargetPicker({
  classId,
  deadlineHint,
  value,
  onChange,
}: SubjectTargetPickerProps) {
  const { data, isLoading } = useQuery(classSubjectsQueryOptions(classId));

  const [allDate, setAllDate] = useState('');

  const pairs = data ?? [];
  const picked = new Map(value.map((item) => [item.subjectId, item]));

  function toggle(subjectId: number, on: boolean) {
    onChange(
      on
        ? [...value, allDate === '' ? { subjectId } : { subjectId, targetDate: allDate }]
        : value.filter((item) => item.subjectId !== subjectId),
    );
  }

  function pickAll(next: string = allDate) {
    onChange(
      pairs.map((pair) =>
        next === ''
          ? { subjectId: pair.subjectId }
          : { subjectId: pair.subjectId, targetDate: next },
      ),
    );
  }

  function stampAll(next: string) {
    setAllDate(next);
    if (value.length > 0) {
      onChange(
        value.map((item) =>
          next === ''
            ? { subjectId: item.subjectId }
            : { subjectId: item.subjectId, targetDate: next },
        ),
      );
    }
  }

  function setTarget(subjectId: number, targetDate: string) {
    onChange(
      value.map((item) =>
        item.subjectId === subjectId
          ? targetDate === ''
            ? { subjectId }
            : { subjectId, targetDate }
          : item,
      ),
    );
  }

  if (classId === undefined) {
    return (
      <p className="rounded-lg border p-4 text-sm text-muted-foreground">
        This project has no class set, so there are no subjects to pick from. Give it a class first.
      </p>
    );
  }

  if (isLoading) {
    return <ListSkeleton />;
  }

  if (pairs.length === 0) {
    return (
      <EmptyState
        title="No subject on this class yet"
        description="Put some on it under Subject Management."
        size="compact"
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-muted/40 p-3">
        <div className="flex-1 min-w-48">
          <label htmlFor="stp-all-date" className="mb-1 block text-xs font-medium">
            Target collection date
          </label>
          <DatePickerInput id="stp-all-date" value={allDate} onChange={stampAll} />
        </div>
        <button
          type="button"
          className="h-9 rounded-md border px-3 text-sm hover:bg-background"
          onClick={() => {
            if (value.length === pairs.length) {
              onChange([]);
            } else {
              pickAll();
            }
          }}
        >
          {value.length === pairs.length ? 'Clear all' : 'Pick all'}
        </button>
      </div>

      <p className="text-xs text-muted-foreground">
        {value.length} of {pairs.length} picked. The date above goes on every one you tick; change a
        row below only where it differs.
      </p>

      <ul className="max-h-72 divide-y overflow-y-auto rounded-lg border">
        {pairs.map((pair) => {
          const chosen = picked.get(pair.subjectId);

          return (
            <li key={pair.classSubjectId} className="flex items-center gap-3 p-2.5">
              <Checkbox
                id={`stp-${pair.subjectId}`}
                checked={chosen !== undefined}
                onCheckedChange={(on) => toggle(pair.subjectId, on === true)}
              />
              <label htmlFor={`stp-${pair.subjectId}`} className="flex-1 cursor-pointer text-sm">
                {pair.subjectName ?? '—'}
                {pair.subjectLocal === null || pair.subjectLocal === ''
                  ? null
                  : ` (${pair.subjectLocal})`}
              </label>

              {chosen === undefined ? null : (
                <div className="w-44 shrink-0">
                  <DatePickerInput
                    id={`stp-date-${pair.subjectId}`}
                    value={chosen.targetDate ?? ''}
                    onChange={(next) => setTarget(pair.subjectId, next)}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {deadlineHint === null ? null : (
        <p className="text-xs text-muted-foreground">
          A subject left without a date takes the project deadline.
        </p>
      )}
    </div>
  );
}
